import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ProductPrice } from "@/components/ui/product-price";
import { cn } from "@/lib/utils";
import { StockStatus } from "./PartsListItem";

export interface ProductInfoRowProps {
  name: string;
  number: string;
  price: number;
  priceFormatted?: string;
  stockStatus: StockStatus;
  isDiscontinued?: boolean;
  productType?: string;
  nameSuffix?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  vendorPartNumber?: string;
  unitOfMeasure?: string;
  unitOfMeasureIsMapped?: boolean;
  showUnmappedFields?: boolean;
}

export function ProductInfoRow({
  name,
  number,
  price,
  priceFormatted,
  stockStatus,
  isDiscontinued,
  productType,
  nameSuffix,
  onClick,
  vendorPartNumber,
  unitOfMeasure,
  unitOfMeasureIsMapped,
  showUnmappedFields,
}: ProductInfoRowProps) {
  return (
    <>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className={cn(
            "font-medium text-sm truncate",
            onClick && "group-hover:text-primary transition-colors"
          )}>
            {name}
          </h3>
          {productType === 'bom' && (
            <Badge className="text-[10px] px-1.5 py-0">Kit</Badge>
          )}
          {nameSuffix}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <p className="text-xs text-muted-foreground">#{number}</p>
          {showUnmappedFields && vendorPartNumber && (
            <>
              <span className="text-muted-foreground/50">•</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-muted-foreground italic cursor-help">{vendorPartNumber}</span>
                  </TooltipTrigger>
                  <TooltipContent><p>Endpoint mapping missing</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </>
          )}
          {unitOfMeasure && (unitOfMeasureIsMapped || showUnmappedFields) && (
            <>
              <span className="text-muted-foreground/50">•</span>
              {unitOfMeasureIsMapped ? (
                <span className="text-xs text-muted-foreground">{unitOfMeasure}</span>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-xs text-muted-foreground italic cursor-help">{unitOfMeasure}</span>
                    </TooltipTrigger>
                    <TooltipContent><p>Endpoint mapping missing</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </>
          )}
          <span className="text-muted-foreground/50">•</span>
          <Badge
            variant="outline"
            className={cn(
              "text-[10px] font-medium px-1.5 py-0",
              stockStatus.type === "in-stock" && "border-transparent bg-transparent text-green-600",
              stockStatus.type === "out-of-stock" && "border-transparent bg-transparent text-red-600",
              stockStatus.type === "backorder" && "border-transparent bg-transparent text-orange-600",
              stockStatus.type === "discontinued" && "bg-white text-red-600 border-red-600"
            )}
          >
            {stockStatus.text}
          </Badge>
        </div>
      </div>

      {!isDiscontinued && (
        <div className="text-right flex-shrink-0 min-w-[70px]">
          <ProductPrice price={price} priceFormatted={priceFormatted} className="font-semibold text-sm text-foreground" />
        </div>
      )}
    </>
  );
}
